import { supabase } from "../../supabase";
import {
  applyOrganizationScope,
  orgScopedInsert,
  orgScopedUpdateByPk,
  resolveOrganizationIdForQueries,
} from "../orgQueryScope";
import {
  readOrgScopedJson,
  resolveCacheOrganizationId,
  writeOrgScopedJson,
} from "../orgCacheStorage";
import { DEFAULT_WORK_TYPES } from "./catalogDefaults";
import {
  sortCatalogItems,
  toDropdownOptions,
  validateCatalogName,
} from "./catalogHelpers";

const TABLE = "work_types";
const ID_COLUMN = "work_type_id";
const CACHE_KEY = "CP_WORK_TYPES_CACHE_V1";

const LEGACY_SELECT_COLUMNS =
  "work_type_id, organization_id, name, display_order, is_active, created_at, updated_at";
const SELECT_COLUMNS = `${LEGACY_SELECT_COLUMNS}, is_default`;

/** Postgres check constraint that keeps the default active (see migration). */
const DEFAULT_MUST_BE_ACTIVE_CONSTRAINT = "work_types_default_must_be_active";

export const ARCHIVE_DEFAULT_BLOCKED_MESSAGE =
  "This is the default Work Type. Set another active Work Type as the default before archiving it.";

export const NO_WORK_TYPES_MESSAGE =
  "No Work Types are set up for your organization. Ask an admin to add a Work Type in Settings → Work Types.";

export const SELECT_WORK_TYPE_MESSAGE = "Select a Work Type before submitting.";

function isMissingIsDefaultColumn(error) {
  const text = `${error?.message || ""} ${error?.details || ""}`;
  return (
    (error?.code === "42703" || error?.code === "PGRST204") &&
    text.includes("is_default")
  );
}

function normalizeWorkTypeRow(row) {
  return {
    work_type_id: row.work_type_id,
    organization_id: row.organization_id ?? null,
    name: row.name,
    display_order: Number(row.display_order ?? 0),
    is_active: row.is_active !== false,
    is_default: row.is_default === true && row.is_active !== false,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function rethrowWorkTypeWriteError(error) {
  if (
    error?.code === "23514" &&
    String(error?.message || "").includes(DEFAULT_MUST_BE_ACTIVE_CONSTRAINT)
  ) {
    throw new Error(ARCHIVE_DEFAULT_BLOCKED_MESSAGE);
  }
  if (error?.code === "23505") {
    throw new Error(
      "A work type with that name already exists in this organization.",
    );
  }
  if (error instanceof Error) {
    throw error;
  }
  throw new Error(error?.message || "Failed to save work type.");
}

export function getFallbackWorkTypes() {
  return DEFAULT_WORK_TYPES.map((entry, index) => ({
    work_type_id: `fallback-${index}`,
    organization_id: null,
    name: entry.name,
    display_order: entry.display_order,
    is_active: true,
    // Fallback rows are not organization rows, so none is a default.
    is_default: false,
    created_at: null,
    updated_at: null,
  }));
}

/**
 * Keep only rows belonging to one organization.
 * Rows without an organization id are excluded so another tenant cannot leak in.
 */
export function workTypesForOrganization(items, organizationId) {
  if (!organizationId) {
    return [];
  }
  return (items || []).filter((row) => row?.organization_id === organizationId);
}

export function workTypesToSessionOptions(items) {
  return toDropdownOptions(items, {
    valueKey: "name",
    labelKey: "name",
    includeEmpty: false,
    activeOnly: true,
  });
}

/**
 * The organization default Work Type name, or "" when there is none.
 * Only an active row that belongs to an organization can be the default;
 * the offline fallback list (no organization id) never supplies a default.
 */
export function resolveDefaultWorkTypeName(items) {
  const active = sortCatalogItems(
    (items || []).filter(
      (row) => row && row.is_active !== false && row.organization_id,
    ),
  );
  const flagged = active.find((row) => row.is_default === true);
  return String((flagged || active[0])?.name || "").trim();
}

/**
 * Work Type to save on a completed session: an explicit employee choice that
 * is still selectable, otherwise the organization default. Never a generic
 * label; "" means nothing valid is available and submit must be blocked.
 */
export function resolveSessionWorkType({
  explicitChoice = "",
  options = [],
  defaultName = "",
} = {}) {
  const values = (options || []).map((option) => String(option?.value ?? ""));
  const explicit = String(explicitChoice || "").trim();
  if (explicit && values.includes(explicit)) {
    return explicit;
  }
  const fallback = String(defaultName || "").trim();
  if (fallback && values.includes(fallback)) {
    return fallback;
  }
  return "";
}

/**
 * Move a work type up or down within its active/archived group, then
 * renumber the full list. Returns null when the move is not possible.
 */
export function reorderWorkTypeDisplayOrders(items, workTypeId, direction) {
  const sorted = sortCatalogItems(items);
  const index = sorted.findIndex((row) => row.work_type_id === workTypeId);
  if (index < 0 || direction === 0) {
    return null;
  }

  const target = sorted[index];
  let swapIndex = index + direction;
  while (
    swapIndex >= 0 &&
    swapIndex < sorted.length &&
    sorted[swapIndex].is_active !== target.is_active
  ) {
    swapIndex += direction;
  }

  if (swapIndex < 0 || swapIndex >= sorted.length) {
    return null;
  }

  const next = [...sorted];
  const displaced = next[swapIndex];
  next[swapIndex] = next[index];
  next[index] = displaced;

  return next.map((row, orderIndex) => ({
    ...row,
    display_order: orderIndex + 1,
  }));
}

function persistSessionCache(organizationId, items) {
  if (!organizationId) {
    return;
  }
  writeOrgScopedJson(CACHE_KEY, organizationId, { items });
}

function readSessionCache(organizationId) {
  if (!organizationId) {
    return null;
  }
  const parsed = readOrgScopedJson(CACHE_KEY, organizationId);
  if (Array.isArray(parsed)) {
    return parsed;
  }
  if (parsed && Array.isArray(parsed.items)) {
    return parsed.items;
  }
  return null;
}

function activeCachedWorkTypes(organizationId) {
  const cached = readSessionCache(organizationId);
  if (!cached) {
    return null;
  }
  return sortCatalogItems(
    workTypesForOrganization(cached.map(normalizeWorkTypeRow), organizationId).filter(
      (row) => row.is_active,
    ),
  );
}

export function readInitialSessionDefaultWorkType(actor) {
  const organizationId = resolveCacheOrganizationId(actor);
  return resolveDefaultWorkTypeName(activeCachedWorkTypes(organizationId) || []);
}

export function readInitialSessionWorkTypeOptions(actor) {
  const organizationId = resolveCacheOrganizationId(actor);
  const cached = activeCachedWorkTypes(organizationId);
  if (cached) {
    return workTypesToSessionOptions(cached);
  }
  return workTypesToSessionOptions(getFallbackWorkTypes());
}

export async function listWorkTypes(client = supabase, options = {}) {
  const { activeOnly = false, organizationId = null, actor = null } = options;

  const resolvedOrgId =
    organizationId ??
    (await resolveOrganizationIdForQueries(actor, client));

  const runQuery = (columns) => {
    let query = client
      .from(TABLE)
      .select(columns)
      .order("display_order", { ascending: true })
      .order("name", { ascending: true });

    query = applyOrganizationScope(query, resolvedOrgId, { tableName: TABLE });

    if (activeOnly) {
      query = query.eq("is_active", true);
    }
    return query;
  };

  let { data, error } = await runQuery(SELECT_COLUMNS);
  if (error && isMissingIsDefaultColumn(error)) {
    // Database without the default Work Type migration yet: keep working,
    // the first active type (display order) acts as the default.
    ({ data, error } = await runQuery(LEGACY_SELECT_COLUMNS));
  }
  if (error) {
    throw error;
  }

  return workTypesForOrganization(
    (data || []).map(normalizeWorkTypeRow),
    resolvedOrgId,
  );
}

/**
 * Active work types for Review / Finalize.
 * A successful empty catalog stays empty (everything archived).
 * Cache, then the historical defaults, are used only when the request fails.
 */
export async function loadWorkTypesForSession(client = supabase, actor = null) {
  let organizationId = null;
  try {
    organizationId = await resolveOrganizationIdForQueries(actor, client);
  } catch (err) {
    console.warn("[workTypeCatalog] organization unresolved", err);
  }

  if (!organizationId) {
    return getFallbackWorkTypes();
  }

  try {
    const rows = await listWorkTypes(client, {
      activeOnly: true,
      actor,
      organizationId,
    });
    persistSessionCache(organizationId, rows);
    return rows;
  } catch (err) {
    console.warn("[workTypeCatalog] load failed — using cache/fallback", err);
  }

  const cached = activeCachedWorkTypes(organizationId);
  if (cached) {
    return cached;
  }

  return getFallbackWorkTypes();
}

export async function refreshWorkTypeSessionCache(client = supabase, actor = null) {
  try {
    await loadWorkTypesForSession(client, actor);
  } catch (err) {
    console.warn("[workTypeCatalog] session cache refresh failed", err);
  }
}

export async function createWorkType(
  { name, display_order },
  client = supabase,
  actor = null,
) {
  const validationError = validateCatalogName(name, "Work type name");
  if (validationError) {
    throw new Error(validationError);
  }

  const { data, error } = await orgScopedInsert(
    client,
    TABLE,
    [
      {
        name: name.trim(),
        display_order: Number(display_order ?? 0),
        is_active: true,
      },
    ],
    actor,
    { select: SELECT_COLUMNS, single: true },
  );

  if (error) {
    rethrowWorkTypeWriteError(error);
  }

  return normalizeWorkTypeRow(data);
}

export async function updateWorkType(
  workTypeId,
  patch,
  client = supabase,
  actor = null,
) {
  if (!actor) {
    throw new Error("Signed-in administrator required.");
  }

  if (patch.name != null) {
    const validationError = validateCatalogName(patch.name, "Work type name");
    if (validationError) {
      throw new Error(validationError);
    }
  }

  const payload = {
    ...(patch.name != null ? { name: patch.name.trim() } : {}),
    ...(patch.display_order != null
      ? { display_order: Number(patch.display_order) }
      : {}),
    ...(patch.is_active != null ? { is_active: patch.is_active } : {}),
  };

  // orgScopedUpdateByPk is async, so awaiting it already runs the update and
  // yields the response ({ data, error }), not a chainable builder. Read the
  // saved row back separately instead of chaining .select() onto the result.
  const result = await orgScopedUpdateByPk(client, TABLE, payload, actor, {
    pkColumn: ID_COLUMN,
    pkValue: workTypeId,
  });

  if (result?.error) {
    rethrowWorkTypeWriteError(result.error);
  }

  const data = await fetchWorkTypeRow(client, workTypeId);

  if (!data) {
    throw new Error("Work type not found in your organization.");
  }

  return normalizeWorkTypeRow(data);
}

async function fetchWorkTypeRow(client, workTypeId) {
  const runQuery = (columns) =>
    client.from(TABLE).select(columns).eq(ID_COLUMN, workTypeId).maybeSingle();
  let { data, error } = await runQuery(SELECT_COLUMNS);
  if (error && isMissingIsDefaultColumn(error)) {
    ({ data, error } = await runQuery(LEGACY_SELECT_COLUMNS));
  }
  if (error) {
    rethrowWorkTypeWriteError(error);
  }
  return data || null;
}

export async function archiveWorkType(workTypeId, client = supabase, actor = null) {
  return updateWorkType(workTypeId, { is_active: false }, client, actor);
}

/**
 * Make one active work type the organization default. The RPC clears the
 * previous default in the same transaction and runs under the caller's RLS
 * (owner/admin only), so two defaults never exist.
 */
export async function setDefaultWorkType(workTypeId, client = supabase, actor = null) {
  if (!actor) {
    throw new Error("Signed-in administrator required.");
  }
  if (!workTypeId) {
    throw new Error("Work type is required.");
  }
  const { data, error } = await client.rpc("cp_set_default_work_type", {
    p_work_type_id: workTypeId,
  });
  if (error) {
    rethrowWorkTypeWriteError(error);
  }
  const row = Array.isArray(data) ? data[0] : data;
  return row ? normalizeWorkTypeRow(row) : null;
}

export async function restoreWorkType(workTypeId, client = supabase, actor = null) {
  return updateWorkType(workTypeId, { is_active: true }, client, actor);
}
